import React, {
  useEffect,
  useRef,
  useCallback,
  SubmitEvent,
  ChangeEvent,
} from "react";
import { useNavigate } from "react-router";

//UI libraries
import {
  Accordion,
  Button,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import DatePicker from "react-datepicker";
import { Typeahead } from "react-bootstrap-typeahead";

//shared components
import NavigationBar from "../navigation/NavigationBar";
import SidebarAdmin from "../sidebars/SidebarAdmin";
import Footer from "../Footer";
import { Labels, PatientLabels } from "../sharedComponents/PatientLabels";
import SurveyStatusModal from "../sharedComponents/SurveyStatusModal";
import LoadingModal from "../sharedComponents/LoadingModal";
import PatientTable from "../sharedComponents/TableComponents/PatientTable";
import ClinicianTable from "../sharedComponents/TableComponents/ClinicianTable";
import { fetchAndShowDiscardInfo } from "../../helper/surveyUtils";

// icons
import { GoChecklist } from "react-icons/go";
import { FiUpload } from "react-icons/fi";

//API services
import {
  editPatient,
  editClinician,
  adminAssignClinician,
  bulkImport,
  bulkValidate,
  activateClinician,
  deactivateClinician,
  adminResetClinicianPassword,
  registerUser,
  PatientRegistrationData,
  ClinicianRegistrationData,
} from "../../services/postFunctions";
import { Diagnosis, Patient } from "../../services/getFunctions";

//custom hooks
import { usePartialState } from "../../helper/Hooks/usePartialState";
import { useAuth } from "../../contexts/AuthContext";
import { useAccordion } from "../../contexts/AccordionContext";
import { usePatientTableState } from "../../helper/Hooks/usePatientTableState";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { useSubmitting } from "../../helper/Hooks/useSubmitting";
import {
  getGermanTodayDateObject,
  handleRawDateInput,
  parseLocalDate,
  formatLocalDate,
} from "../../helper/dateHelpers";

//others
import { FormControlElement } from "../../types/uiTypes";
import { toErrorType } from "../../helper/alertBoxes";

import "../../custom.css";
import "react-bootstrap-typeahead/css/Typeahead.css";
import "react-datepicker/dist/react-datepicker.css";

interface Item {
  value: string;
  valid: boolean;
  key: string;
  message: string;
}

interface BulkDataPatient {
  items: Item[];
}

interface BulkData {
  patients: BulkDataPatient[];
  valid: boolean;
}

export interface GenderList {
  value: string;
  name: string;
}

export interface HomeClinicAdminProps {
  title: string;
}

interface HomeClinicAdminState {
  //Configuration
  userType: string;

  // Modal states
  modalOpen: boolean;
  modalBulkOpen: boolean;
  modalClinicianOpen: boolean;
  modalAssignClinicianOpen: boolean;
  modalEditPatientOpen: boolean;
  modalEditClinicianOpen: boolean;
  modalShowPatientListOpen: boolean;
  modalTransferPatientsOpen: boolean;
  modalDiagnosisOpen: boolean;
  modalSurveyStateOpen: boolean;
  modalValidOpen: boolean;

  // Loading states
  importing: boolean;
  validation: boolean;
  bulkValidation: boolean;

  // Date states
  admission: Date;
  editAdmission: Date;
  discharge: Date;
  editDischarge: Date;
  admissionChanged: boolean;
  dischargeChanged: boolean;
  birthday: Date | null;
  editBirthday: Date;
  dateChangeMessage: string;

  // Form states
  clinician: string;
  clinicianId: string;
  code: string;
  diagnosesPatient: Diagnosis[];
  gender: string;
  usernameClinician: string;
  username: string;
  clinicId: string;
  clinicName: string;
  patientId: string;
  clinicianFromId: string;
  clinicianUsername: string;
  clinicianFromUsername: string;
  clinicianEnabled: boolean;
  editPatientButton: string;
  bulkImportButton: string;
  isClinicianModalEdited: boolean;

  // Data lists
  genderList: GenderList[];
  labels: Labels;
  patientList: string[];

  // Other states
  currentPatient: Patient;
  bulkData: BulkData;
}

const HomeClinicAdmin = ({ title }: HomeClinicAdminProps) => {
  // router v6 hooks
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { state: appState, handleAccordion } = useAccordion();
  const { isSubmitting: isCreatingClinician, wrap: wrapClinician } =
    useSubmitting();
  const { isSubmitting: isCreatingPatient, wrap: wrapPatient } =
    useSubmitting();

  const {
    showAlertModal,
    showConfirmModal,
    showPasswordModal,
    reportNewPasswordError,
    reportDisableEnableClinicianError,
    reportEditClinicianError,
    reportClinicianAssignError,
    reportEditPatientError,
    reportRegistError,
    reportCsvValidationError,
    showNewClinicianPasswordModal,
  } = useAlertHelpers();

  const {
    patients,
    pagination,
    diagnoses,
    clinicians,
    isLoading,
    refreshCurrentPage,
    loadPage,
    updateDateFilter,
    updateFieldFilter,
    updateLabelFilter,
    updateReportStateFilter,
    handlePageChange,
    handlePageSizeChange,
    updateSort,
  } = usePatientTableState();

  const initialState: HomeClinicAdminState = {
    userType: "admin",

    // Modal states
    modalOpen: false,
    modalBulkOpen: false,
    modalClinicianOpen: false,
    modalAssignClinicianOpen: false,
    modalEditPatientOpen: false,
    modalEditClinicianOpen: false,
    modalShowPatientListOpen: false,
    modalTransferPatientsOpen: false,
    modalDiagnosisOpen: false,
    modalSurveyStateOpen: false,
    modalValidOpen: false,

    // Loading states
    importing: false,
    validation: false,
    bulkValidation: false,

    // Date states
    admission: getGermanTodayDateObject(),
    editAdmission: getGermanTodayDateObject(),
    discharge: getGermanTodayDateObject(),
    editDischarge: getGermanTodayDateObject(),
    admissionChanged: false,
    dischargeChanged: false,
    birthday: null,
    editBirthday: getGermanTodayDateObject(),
    dateChangeMessage: "",

    // Form states
    clinician: "",
    clinicianId: "",
    code: "",
    diagnosesPatient: [],
    gender: "",
    usernameClinician: "",
    username: "",
    clinicId: "",
    clinicName: "",
    patientId: "",
    clinicianFromId: "",
    clinicianUsername: "",
    clinicianFromUsername: "",
    clinicianEnabled: true,
    editPatientButton: "",
    bulkImportButton: "",
    isClinicianModalEdited: false,

    // Data lists
    genderList: [
      { value: "FEMALE", name: "Weiblich" },
      { value: "MALE", name: "Männlich" },
      { value: "DIVERSE", name: "Divers" },
    ],
    labels: [],
    patientList: [],

    // Other states
    currentPatient: {
      viewed: "",
      duplicate: false,
      date_of_birth: "",
      admission: "",
      discharge: "",
      code: "",
      clinician_username: "",
      clinician_id: "",
      diagnoses: [],
      gender: "",
      id: "",
      survey_statuses: [],
      survey_time_windows: [],
      labels: [],
      last_update: 0,
    },
    bulkData: {
      patients: [],
      valid: false,
    },
  };

  const [state, updateState] = usePartialState(initialState);

  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * Initialize the whole component:
   * - Sets the document title
   * - Check the admin's authentication status and redirects when fails
   * - Initializes the states data:patient list, clinician list and diagnoses
   */
  useEffect(() => {
    document.title = title;

    if (!authState.isAuthenticated) {
      navigate("/loginadmin", { replace: true });
      return;
    }

    if (authState.isAuthenticated) {
      loadPage(1, 100);
    }
  }, [title, authState.isAuthenticated, navigate, loadPage]);

  // modal handlers
  const openModal = useCallback(() => {
    updateState({
      labels: [],
      diagnosesPatient: [],
      modalOpen: true,
    });
  }, [updateState]);

  const closeModal = useCallback(() => {
    updateState({
      modalOpen: false,
      code: "",
      gender: "",
      diagnosesPatient: [],
      clinician: "",
      birthday: null,
      admission: getGermanTodayDateObject(),
      discharge: getGermanTodayDateObject(),
    });
  }, [updateState]);

  const openBulkModal = useCallback(() => {
    updateState({ modalBulkOpen: true });
  }, [updateState]);

  const closeBulkModal = useCallback(() => {
    updateState({
      modalBulkOpen: false,
      bulkValidation: false,
      validation: false,
    });
  }, [updateState]);

  const openValidModal = useCallback(() => {
    updateState({
      modalValidOpen: true,
      validation: false,
    });
  }, [updateState]);

  const closeValidModal = useCallback(() => {
    updateState({ modalValidOpen: false });
  }, [updateState]);

  const openClinicianModal = useCallback(() => {
    updateState({ modalClinicianOpen: true });
  }, [updateState]);

  const closeClinicianModal = useCallback(() => {
    updateState({ modalClinicianOpen: false });
  }, [updateState]);

  const closeDiagnosisModal = useCallback(() => {
    updateState({ modalDiagnosisOpen: false });
  }, [updateState]);

  const openAssignClinicianModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const code = event.currentTarget.id;
      const patient = patients.find((p) => p.code === code);

      if (patient) {
        updateState({
          modalAssignClinicianOpen: true,
          patientId: patient.id,
          code: patient.code,
          clinicianFromId: patient.clinician_id,
          clinicianFromUsername: patient.clinician_username,
        });
      }
    },
    [patients, updateState],
  );

  const closeAssignClinicianModal = useCallback(() => {
    updateState({
      modalAssignClinicianOpen: false,
      code: "",
    });
  }, [updateState]);

  /**Set modalEditPatientOpen on true, if the modal is opened.
   * Set the actual states of the selected patient.
   */
  const openEditPatientModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();

      const code = event.currentTarget.id;
      const patient = patients.find((p) => p.code === code);

      if (!patient) {
        console.error(`Patient with code ${code} not found`);
        return; // Exit if the patient is not found
      }

      let newBirthday: Date;
      let newAdmission: Date;
      let newDischarge: Date;
      try {
        newBirthday = parseLocalDate(patient.date_of_birth);
        newAdmission = parseLocalDate(patient.admission);
        newDischarge = parseLocalDate(patient.discharge);
      } catch (error) {
        console.error("Failed to parse patient dates:", error);
        return;
      }

      let genderValue = patient.gender;
      if (patient.gender === "Weiblich") {
        genderValue = "FEMALE";
      } else if (patient.gender === "Männlich") {
        genderValue = "MALE";
      } else if (patient.gender === "Divers") {
        genderValue = "DIVERSE";
      }

      const patientDiagnoses = patient.diagnoses || [];

      updateState({
        modalEditPatientOpen: true,
        editBirthday: newBirthday,
        editAdmission: newAdmission,
        editDischarge: newDischarge,
        admissionChanged: false,
        dischargeChanged: false,
        clinicianId: patient.clinician_id,
        clinicianUsername: patient.clinician_username,
        diagnosesPatient: patientDiagnoses,
        patientId: patient.id,
        gender: genderValue,
        code: patient.code,
        labels: patient.labels,
        dateChangeMessage: "",
        currentPatient: patient,
      });
    },
    [patients, updateState],
  );

  const closeEditPatientModal = useCallback(() => {
    updateState({
      modalEditPatientOpen: false,
      code: "",
      gender: "",
      dateChangeMessage: "",
    });
  }, [updateState]);

  const openEditClinicianModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const index = parseInt(event.currentTarget.id);
      const clinician = clinicians[index];

      if (clinician) {
        updateState({
          modalEditClinicianOpen: true,
          clinicianEnabled: clinician.enabled,
          clinicianId: clinician.id,
          clinicianUsername: clinician.username,
          isClinicianModalEdited: false,
        });
      }
    },
    [clinicians, updateState],
  );

  const closeEditClinicianModal = useCallback(() => {
    updateState({
      modalEditClinicianOpen: false,
      clinicianId: "",
      clinicianUsername: "",
      isClinicianModalEdited: false,
    });
  }, [updateState]);

  const openShowPatientListModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const index = parseInt(event.currentTarget.id);
      const clinician_username = clinicians[index]?.username;

      if (clinician_username) {
        const patientCodes = patients
          .filter((p) => p.clinician_username === clinician_username)
          .map((p) => p.code);

        updateState({
          modalShowPatientListOpen: true,
          clinicianUsername: clinician_username,
          patientList: patientCodes,
        });
      }
    },
    [clinicians, patients, updateState],
  );

  const closeShowPatientListModal = useCallback(() => {
    updateState({ modalShowPatientListOpen: false });
  }, [updateState]);

  const openTransferPatientsModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const index = parseInt(event.currentTarget.id);
      const clinician = clinicians[index];

      if (clinician) {
        const patientIds = patients
          .filter((p) => p.clinician_username === clinician.username)
          .map((p) => p.id);

        updateState({
          modalTransferPatientsOpen: true,
          clinicianFromId: clinician.id,
          clinicianFromUsername: clinician.username,
          patientList: patientIds,
        });
      }
    },
    [clinicians, patients, updateState],
  );

  const closeTransferPatientsModal = useCallback(() => {
    updateState({ modalTransferPatientsOpen: false });
  }, [updateState]);

  const openSurveyStateModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const id = event.currentTarget.id;
      const patient = patients.find((p) => p.id === id);

      if (patient) {
        if (state.currentPatient.id !== id) {
          updateState({
            currentPatient: patient,
            modalSurveyStateOpen: true,
          });
        }
      } else {
        console.error(`Patient with id ${id} not found.`);
      }
    },
    [patients, state.currentPatient, updateState],
  );

  const closeSurveyStateModal = useCallback(() => {
    updateState({
      modalSurveyStateOpen: false,
    });
  }, [updateState]);

  // form handlers
  const handleCode = useCallback(
    (event: ChangeEvent<FormControlElement>) => {
      updateState({ code: event.target.value });
    },
    [updateState],
  );

  const handleBirthdate = useCallback(
    (date: Date | null) => {
      if (date) {
        updateState({ birthday: date });
      }
    },
    [updateState],
  );

  const handleGender = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      updateState({ gender: value === "Auswählen..." ? "" : value });
    },
    [updateState],
  );

  const handleClinician = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      updateState({ clinician: value === "Auswählen..." ? "" : value });
    },
    [updateState],
  );

  const handleDiagnosis = useCallback(
    (selectedItems) => {
      const items = selectedItems as Diagnosis[];
      if (items && Array.isArray(items) && items.length > 0) {
        updateState({
          diagnosesPatient: items.filter(
            (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i,
          ),
        });
      } else {
        updateState({ diagnosesPatient: [] });
      }
    },
    [updateState],
  );

  const handleAdmissionDate = useCallback(
    (date: Date | null) => {
      if (date) {
        updateState({ admission: date });
      }
    },
    [updateState],
  );

  const handleDischargeDate = useCallback(
    (date: Date | null) => {
      if (date) {
        if (date <= state.admission) {
          showAlertModal(
            "Das Entlassdatum muss nach dem Aufnahmedatum liegen.",
          );
        } else {
          updateState({ discharge: date });
        }
      }
    },
    [state.admission, updateState, showAlertModal],
  );

  const handleUsername = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      updateState({ usernameClinician: event.target.value });
    },
    [updateState],
  );

  //avoid pass null to data or show alert when clearing date
  const handleEditBirthdate = useCallback(
    (date: Date | null) => {
      if (date) {
        updateState({ editBirthday: date });
      }
    },
    [updateState],
  );

  const handleEditAdmissionDate = useCallback(
    (date: Date | null) => {
      if (date) {
        updateState({
          dateChangeMessage:
            "Achtung: Durch das Ändern von Aufnahme- oder Entlassdatum können bisherige Befragungen ungültig werden. Alle Datumsangaben in deutscher Ortszeit (MEZ/MESZ).",
          editAdmission: date,
          admissionChanged: true,
        });
      }
    },
    [updateState],
  );

  const handleEditAdmissionCommit = useCallback(async () => {
    if (!state.admissionChanged) {
      return;
    }
    const date = state.editAdmission;
    if (!date) return;

    if (date >= state.editDischarge) {
      updateState({ admissionChanged: false });
      showAlertModal("Das Entlassdatum muss nach dem Aufnahmedatum liegen.");
      return;
    }
    updateState({ admissionChanged: false });

    await fetchAndShowDiscardInfo(
      state.patientId,
      date,
      state.editDischarge,
      showAlertModal,
    );
  }, [
    state.admissionChanged,
    state.editAdmission,
    state.editDischarge,
    state.patientId,
    updateState,
    showAlertModal,
  ]);

  const handleEditDischargeDate = useCallback(
    (date: Date | null) => {
      if (date) {
        updateState({
          dateChangeMessage:
            "Achtung: Durch das Ändern von Aufnahme- oder Entlassdatum können bisherige Befragungen ungültig werden. Alle Datumsangaben in deutscher Ortszeit (MEZ/MESZ).",
          editDischarge: date,
          dischargeChanged: true,
        });
      }
    },
    [updateState],
  );

  const handleEditDischargeCommit = useCallback(async () => {
    if (!state.dischargeChanged) {
      return;
    }
    const date = state.editDischarge;
    if (!date) return;

    if (date <= state.editAdmission) {
      updateState({ dischargeChanged: false });
      showAlertModal("Das Entlassdatum muss nach dem Aufnahmedatum liegen.");
      return;
    }
    updateState({ dischargeChanged: false });

    await fetchAndShowDiscardInfo(
      state.patientId,
      state.editAdmission,
      date,
      showAlertModal,
    );
  }, [
    state.dischargeChanged,
    state.editAdmission,
    state.editDischarge,
    state.patientId,
    updateState,
    showAlertModal,
  ]);

  const handleClinicianUsername = useCallback(
    (event: ChangeEvent<FormControlElement>) => {
      updateState({
        clinicianUsername: event.target.value,
        isClinicianModalEdited: true,
      });
    },
    [updateState],
  );

  //business logic handlers

  /**Method to transfer all patients from one clinician to another clinician. */
  const transferPatients = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (state.clinician !== "") {
        try {
          // filter patients that belong to the clinician
          const patientsToTransfer = patients.filter(
            (p) => p.clinician_id === state.clinicianFromId,
          );

          const promises = patientsToTransfer.map((patient) => {
            const assignmentData = {
              patient_id: patient.id,
              clinician_from: state.clinicianFromId,
              clinician_to: state.clinician,
            };
            return adminAssignClinician(assignmentData);
          });

          await Promise.all(promises);

          showAlertModal(
            `Die Patient*innen wurden erfolgreich an den*die Mitarbeitende*n ${state.clinician} übertragen.`,
          );
          closeTransferPatientsModal();
          await refreshCurrentPage();
        } catch (error) {
          reportClinicianAssignError(toErrorType(error));
        }
      } else {
        showAlertModal(
          "Sie müssen noch eine*n Mitarbeitende*n auswählen, an welchen die Patient*innen übertragen werden sollen.",
        );
      }
    },
    [
      patients,
      state.clinician,
      state.clinicianFromId,
      closeTransferPatientsModal,
      showAlertModal,
      reportClinicianAssignError,
      refreshCurrentPage,
    ],
  );

  /**Method to reset clinicians password. */
  const resetPassword = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const index = parseInt(event.currentTarget.id);
      const clinician_id = clinicians[index]?.id;

      if (clinician_id) {
        showConfirmModal(
          "Sind Sie sicher, dass sie das Passwort zurücksetzen wollen?",
          async () => {
            try {
              const password = await adminResetClinicianPassword(clinician_id);
              showPasswordModal(password);
            } catch (error) {
              reportNewPasswordError(toErrorType(error));
            }
          },
        );
      }
    },
    [clinicians, showPasswordModal, reportNewPasswordError, showConfirmModal],
  );

  /**Method to enable or disable a clinician.*/
  const disableEnableClinician = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      const index = event.currentTarget.id;
      const clinician = clinicians[index];

      if (clinician) {
        const {
          id: clinician_id,
          username: clinician_username,
          enabled: clinician_enable,
        } = clinician;
        const str = clinician_enable ? "deaktiviert" : "aktiviert";
        const activationDeactivationFunction = clinician_enable
          ? deactivateClinician
          : activateClinician;

        try {
          await activationDeactivationFunction(clinician_id);
          showAlertModal(
            `Der*Die Mitarbeitende ${clinician_username} wurde erfolgreich ${str}.`,
          );
          await refreshCurrentPage();
        } catch (error) {
          reportDisableEnableClinicianError(toErrorType(error));
        }
      }
    },
    [
      clinicians,
      refreshCurrentPage,
      showAlertModal,
      reportDisableEnableClinicianError,
    ],
  );

  /**Method for handle edit clinician.
   * The input data are stringified to JSON.
   * Then send JSON to backend api.
   */
  const handleEditClinician = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!state.isClinicianModalEdited) {
        showAlertModal("Sie müssen einen Benutzernamen eingeben. ");
        return;
      }

      if (!state.clinicianUsername.trim()) {
        showAlertModal("Sie müssen einen Benutzernamen angeben.");
      }

      const clinicianData = {
        enabled: state.clinicianEnabled,
        id: state.clinicianId,
        username: state.clinicianUsername,
      };

      try {
        await editClinician(clinicianData);
        showAlertModal("Der Benutzername wurde erfolgreich geändert.");
        closeEditClinicianModal();
        await refreshCurrentPage();
      } catch (error) {
        reportEditClinicianError(toErrorType(error));
      }
    },
    [
      state.isClinicianModalEdited,
      state.clinicianEnabled,
      state.clinicianId,
      state.clinicianUsername,
      closeEditClinicianModal,
      refreshCurrentPage,
      showAlertModal,
      reportEditClinicianError,
    ],
  );

  /**Method for handle clinician assign.
   * The input data are stringified to JSON.
   * Then send JSON to backend api.
   */
  const handleClinicianAssign = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();

      const assignmentData = {
        patient_id: state.patientId,
        clinician_from: state.clinicianFromId,
        clinician_to: state.clinician,
      };

      try {
        await adminAssignClinician(assignmentData);
        showAlertModal(
          "Dem Patienten wurde erfolgreich ein neuer Mitarbeiter zugewiesen.",
        );
        closeAssignClinicianModal();
        await refreshCurrentPage();
      } catch (error) {
        reportClinicianAssignError(toErrorType(error));
      }
    },
    [
      state.patientId,
      state.clinicianFromId,
      state.clinician,
      closeAssignClinicianModal,
      refreshCurrentPage,
      showAlertModal,
      reportClinicianAssignError,
    ],
  );

  /**Method for handle patients diagnoses.
   * Each diagnoses that is selected is written to the diagnosesPatient list.
   * If no diagnosis is selected, an alert is displayed.
   */
  const handleEditDiagnosesList = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      const checkboxes = Array.from(
        event.currentTarget.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"]:checked',
        ),
      );

      const diagnoses: Diagnosis[] = checkboxes.map((checkbox) => ({
        description: checkbox.dataset.name || "",
        id: checkbox.value,
      }));

      if (diagnoses.length === 0) {
        showAlertModal("Sie müssen mindestens eine Diagnose wählen.");
      } else {
        updateState({ diagnosesPatient: diagnoses });
        closeDiagnosisModal();
      }
    },
    [closeDiagnosisModal, updateState, showAlertModal],
  );

  /**Method for handle patient edit.*/
  const handleEditPatient = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (state.admissionChanged) {
        await handleEditAdmissionCommit();
        return;
      }
      if (state.dischargeChanged) {
        await handleEditDischargeCommit();
        return;
      }
      if (!state.editBirthday || !state.editAdmission || !state.editDischarge) {
        showAlertModal("Bitte füllen Sie alle Datumsfelder aus.");
        return;
      }

      if (state.editDischarge <= state.editAdmission) {
        showAlertModal("Das Entlassdatum muss nach dem Aufnahmedatum liegen.");
        return;
      }

      const patientData = {
        date_of_birth: formatLocalDate(state.editBirthday),
        admission: formatLocalDate(state.editAdmission),
        discharge: formatLocalDate(state.editDischarge),
        clinician_id: state.clinicianId,
        clinician_username: state.clinicianUsername,
        diagnoses: state.diagnosesPatient,
        id: state.patientId,
        gender: state.gender,
        code: state.code,
        labels: state.labels,
        last_update: state.currentPatient.last_update,
      };

      try {
        await editPatient(patientData);
        closeEditPatientModal();
        await refreshCurrentPage();
      } catch (error) {
        reportEditPatientError(toErrorType(error));
      }
    },
    [
      state.admissionChanged,
      state.dischargeChanged,
      state.editDischarge,
      state.editAdmission,
      state.editBirthday,
      state.clinicianId,
      state.clinicianUsername,
      state.diagnosesPatient,
      state.patientId,
      state.gender,
      state.code,
      state.labels,
      state.currentPatient.last_update,
      closeEditPatientModal,
      refreshCurrentPage,
      reportEditPatientError,
      showAlertModal,
      handleEditAdmissionCommit,
      handleEditDischargeCommit,
    ],
  );

  /**
   * Method for handle the patient regist.
   * The input data are stringified to JSON.
   * Then send JSON to backend API.
   */
  const handleSubmit = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      wrapPatient(async () => {
        if (!state.code.trim()) {
          showAlertModal("Sie müssen einen Benutzernamen angeben.");
        } else if (state.gender === "") {
          showAlertModal("Sie müssen ein Geschlecht auswählen.");
        } else if (state.clinician === "") {
          showAlertModal("Sie müssen eine*n Mitarbeitende*n auswählen.");
        } else if (state.diagnosesPatient?.length === 0) {
          showAlertModal("Sie müssen eine Diagnose auswählen.");
        } else if (
          formatLocalDate(state.discharge) <= formatLocalDate(state.admission)
        ) {
          showAlertModal(
            "Das Entlassdatum muss nach dem Aufnahmedatum liegen.",
          );
        } else if (!state.birthday) {
          showAlertModal("Bitte geben Sie ein Geburtsdatum ein!");
        } else {
          const patientRegistationData: PatientRegistrationData = {
            code: state.code,
            day_of_birth: formatLocalDate(state.birthday),
            gender: state.gender,
            clinician_username: state.clinician,
            admission: formatLocalDate(state.admission),
            diagnosis_ids: [state.diagnosesPatient[0].id],
            discharge: formatLocalDate(state.discharge),
            labels: state.labels,
          };

          try {
            await registerUser(patientRegistationData, "patient");
            if (state.editPatientButton === "btn1") {
              closeModal();
            } else {
              openModal();
            }

            // Reset form
            updateState({
              code: "",
              gender: "",
              diagnosesPatient: [],
              clinician: "",
              birthday: null,
              admission: getGermanTodayDateObject(),
              discharge: getGermanTodayDateObject(),
              labels: [],
            });

            await refreshCurrentPage();
          } catch (error) {
            reportRegistError(toErrorType(error));
          }
        }
      });
    },
    [
      state.code,
      state.gender,
      state.clinician,
      state.diagnosesPatient,
      state.discharge,
      state.admission,
      state.birthday,
      state.labels,
      state.editPatientButton,
      closeModal,
      openModal,
      refreshCurrentPage,
      updateState,
      reportRegistError,
      showAlertModal,
      wrapPatient,
    ],
  );

  /**Method for handle the validation of patient bulk import.
   */
  const validateBulkImport = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();

      const file = fileInput.current?.files?.[0];
      if (!file) {
        showAlertModal(
          "Sie müssen zuerst eine Datei zur Überprüfung auswählen.",
        );
        return;
      }

      updateState({ validation: true });
      try {
        const response = await bulkValidate(file);
        updateState({
          bulkData: response.data,
          bulkValidation: response.data.valid,
        });

        openValidModal();
      } catch (error) {
        updateState({ validation: false });
        reportCsvValidationError(toErrorType(error));
      }
    },
    [openValidModal, updateState, showAlertModal, reportCsvValidationError],
  );

  /**
   * Method for handle the patient bulk import.
   */
  const handleBulkImport = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();

      const file = fileInput.current?.files?.[0];

      if (state.bulkImportButton === "validation") {
        await validateBulkImport(event);
        return;
      }

      updateState({
        validation: false,
        importing: true,
      });
      try {
        await bulkImport(file);
        showAlertModal("Die Patient*innen wurden erfolgreich importiert.");

        updateState({
          bulkValidation: false,
          importing: false,
        });

        closeBulkModal();
        await refreshCurrentPage();
      } catch (error) {
        updateState({ importing: false });
        console.error(error);
        showAlertModal(
          "Irgendetwas ist schief gelaufen. Die Patient*innen wurden nicht importiert.",
        );
      }
    },
    [
      validateBulkImport,
      state.bulkImportButton,
      closeBulkModal,
      refreshCurrentPage,
      updateState,
      showAlertModal,
    ],
  );

  /**Method for handle the clinician regist
   */
  const handleClinicianRegist = useCallback(
    async (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      wrapClinician(async () => {
        //check only the case when name is null
        if (!state.usernameClinician.trim()) {
          showAlertModal("Sie müssen einen Benutzernamen angeben.");
          return;
        }

        const clinicianRegistraionData: ClinicianRegistrationData = {
          username: state.usernameClinician,
        };

        try {
          const response = await registerUser(
            clinicianRegistraionData,
            "clinician",
          );
          showNewClinicianPasswordModal(response.data);
          closeClinicianModal();
          await refreshCurrentPage();
        } catch (error) {
          reportRegistError(toErrorType(error));
        }
      });
    },
    [
      state.usernameClinician,
      closeClinicianModal,
      refreshCurrentPage,
      reportRegistError,
      showAlertModal,
      showNewClinicianPasswordModal,
      wrapClinician,
    ],
  );

  return (
    <div className="header-fixed" id="body">
      {/* navigation bar (bar on the top) */}
      <NavigationBar />
      <div className="container-fluid">
        <div className="row">
          {/* Sidebar (bar on the side) */}
          <SidebarAdmin />
          {/* Content */}
          <div className="col container p-3 " id="main-content">
            <Accordion className="pb-2" activeKey={appState.admin.patients}>
              <Accordion.Item eventKey="0">
                <Accordion.Header
                  onClick={() =>
                    handleAccordion({
                      input: "patients",
                      userType: state.userType,
                    })
                  }
                >
                  Übersicht Patient*innen
                </Accordion.Header>
                <Accordion.Body>
                  <Button className="ms-2" onClick={openModal}>
                    Patient*in anlegen
                  </Button>

                  <Button className="ms-2" onClick={openBulkModal}>
                    Sammelimport
                  </Button>

                  <PatientTable
                    patients={patients}
                    userType={state.userType}
                    pagination={pagination}
                    openEditPatientModal={openEditPatientModal}
                    openAssignClinicianModal={openAssignClinicianModal}
                    openResultModal={() => {}}
                    openSurveyStateModal={openSurveyStateModal}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    updateStringFilter={updateFieldFilter}
                    updateSelectFilter={updateFieldFilter}
                    updateDateFilter={updateDateFilter}
                    updateLabelFilter={updateLabelFilter}
                    updateReportStateFilter={updateReportStateFilter}
                    updateSort={updateSort}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <Accordion className="pb-2" activeKey={appState.admin.employees}>
              <Accordion.Item eventKey="1">
                <Accordion.Header
                  onClick={() =>
                    handleAccordion({ input: "employees", userType: "admin" })
                  }
                >
                  Übersicht Mitarbeitende
                </Accordion.Header>
                <Accordion.Body>
                  <Button className="ms-2" onClick={openClinicianModal}>
                    Mitarbeitende anlegen
                  </Button>

                  <ClinicianTable
                    clinicians={clinicians}
                    patients={patients}
                    openEditClinicianModal={openEditClinicianModal}
                    openShowPatientListModal={openShowPatientListModal}
                    openTransferPatientsModal={openTransferPatientsModal}
                    disableEnableClinician={disableEnableClinician}
                    resetPassword={resetPassword}
                  />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {state.modalSurveyStateOpen && (
              <div>
                <SurveyStatusModal
                  currentPatient={state.currentPatient}
                  modalSurveyStateOpen={state.modalSurveyStateOpen}
                  closeSurveyStateModal={closeSurveyStateModal}
                />
              </div>
            )}

            <Modal
              backdrop="static"
              show={state.modalEditClinicianOpen}
              onHide={closeEditClinicianModal}
            >
              <ModalHeader closeButton>
                <ModalTitle>Mitarbeitenden-ID ändern</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={handleEditClinician}>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="5" htmlFor="old-clinician">
                      Alte Mitarbeitenden-ID:
                    </Form.Label>
                    <Col sm="7">
                      <Form.Control
                        id="old-clinician"
                        className="form-control-plain"
                        plaintext
                        readOnly
                        defaultValue={state.clinicianUsername}
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group
                    as={Row}
                    className="mb-3"
                    controlId="new-clinician"
                  >
                    <Form.Label column sm="5">
                      Neue Mitarbeitenden-ID:{" "}
                    </Form.Label>
                    <Col sm="7">
                      <Form.Control
                        className="form-control"
                        required
                        type="username"
                        onChange={handleClinicianUsername}
                      />
                    </Col>
                  </Form.Group>
                  <Button type="submit">Speichern</Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeEditClinicianModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              backdrop="static"
              show={state.modalEditPatientOpen}
              onHide={closeEditPatientModal}
            >
              <ModalHeader closeButton>
                <ModalTitle>
                  Patient*in <em>{state.code}</em> bearbeiten
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={handleEditPatient}>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="formGridBirthday">
                        <Form.Label>Geburtsdatum</Form.Label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Datum auswählen"
                          dateFormat="dd.MM.yyyy"
                          selected={state.editBirthday || null}
                          onChange={(date) => {
                            handleEditBirthdate(date);
                          }}
                          onChangeRaw={handleRawDateInput}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="gender-select">
                        <Form.Label>Geschlecht</Form.Label>
                        <Form.Select
                          className="form-control"
                          required
                          value={state.gender}
                          onChange={handleGender}
                        >
                          {/*  {state.gender === "MALE" ? (
                                <option>Männlich</option>
                              ) : state.gender === "FEMALE" ? (
                                <option>Weiblich</option>
                              ) : (
                                <option>Divers</option>
                              )}
                              */}
                          {state.genderList &&
                            state.genderList.length > 0 &&
                            state.genderList.map((gender) => {
                              return (
                                <option key={gender.value} value={gender.value}>
                                  {gender.name}
                                </option>
                              );
                            })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label htmlFor="diagnose-input">
                          Diagnose(n)
                        </Form.Label>
                        <Typeahead
                          id="basic-typeahead-example"
                          inputProps={{
                            id: "diagnose-input",
                          }}
                          selected={state.diagnosesPatient || []}
                          multiple={true}
                          size="sm"
                          align={"left"}
                          labelKey="description"
                          options={(diagnoses as Diagnosis[]) || []}
                          placeholder="Auswählen..."
                          clearButton={false}
                          onChange={handleDiagnosis}
                          maxResults={1000}
                          renderMenuItemChildren={(option) => (
                            <span className="text-wrap">
                              <b>{(option as Diagnosis).id}</b> -{" "}
                              {(option as Diagnosis).description}
                            </span>
                          )}
                          filterBy={(option, searchText) => {
                            return (
                              (option as Diagnosis).id
                                .toLowerCase()
                                .includes(searchText.text.toLowerCase()) ||
                              (option as Diagnosis).description
                                .toLowerCase()
                                .includes(searchText.text.toLowerCase())
                            );
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Aufnahmedatum ändern</Form.Label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Datum auswählen"
                          selected={state.editAdmission || null}
                          onChange={handleEditAdmissionDate}
                          onCalendarClose={handleEditAdmissionCommit}
                          dateFormat="dd.MM.yyyy"
                          onChangeRaw={handleRawDateInput}
                          preventOpenOnFocus
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Entlassdatum ändern</Form.Label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Datum auswählen"
                          selected={state.editDischarge || null}
                          minDate={state.editAdmission}
                          onChange={handleEditDischargeDate}
                          onCalendarClose={handleEditDischargeCommit}
                          dateFormat="dd.MM.yyyy"
                          onChangeRaw={handleRawDateInput}
                          preventOpenOnFocus
                        />
                      </Form.Group>
                    </Col>
                    {state.dateChangeMessage && (
                      <Form.Text className="text-danger mt-2">
                        {state.dateChangeMessage}
                      </Form.Text>
                    )}
                  </Row>

                  <PatientLabels
                    labels={state.labels}
                    setLabels={(labels) => updateState({ labels })}
                  />
                  <Button type="submit">Speichern</Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeEditPatientModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>
            {
              // Modification modal
            }
            <Modal
              backdrop="static"
              size="lg"
              show={state.modalDiagnosisOpen}
              onHide={closeDiagnosisModal}
            >
              <ModalHeader>Wählen Sie die Diagnose(n) aus:</ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={handleEditDiagnosesList}>
                  {diagnoses &&
                    diagnoses.length > 0 &&
                    diagnoses.map((diagnosis) => {
                      return (state.diagnosesPatient || []).some(
                        (diagPatient) => {
                          return (
                            diagnosis.description === diagPatient.description
                          );
                        },
                      ) ? (
                        <Form.Check
                          key={diagnosis.id}
                          type="checkbox"
                          id={diagnosis.id}
                          data-name={diagnosis.description}
                          label={diagnosis.description}
                          value={diagnosis.id}
                          defaultChecked
                        />
                      ) : (
                        <Form.Check
                          key={diagnosis.id}
                          type="checkbox"
                          id={diagnosis.id}
                          data-name={diagnosis.description}
                          label={diagnosis.description}
                          value={diagnosis.id}
                        />
                      );
                    })}
                  <Button type="submit">Speichern</Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeDiagnosisModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              backdrop="static"
              show={state.modalShowPatientListOpen}
              onHide={closeShowPatientListModal}
            >
              <ModalHeader closeButton>
                <ModalTitle>
                  Patient*innenliste von <em>{state.clinicianUsername}</em>
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form noValidate>
                  {state.patientList &&
                    state.patientList.length > 0 &&
                    state.patientList.map((patient, index) => {
                      return <div key={index}>{patient}</div>;
                    })}
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeShowPatientListModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              backdrop="static"
              show={state.modalTransferPatientsOpen}
              onHide={closeTransferPatientsModal}
            >
              <ModalHeader closeButton>
                <ModalTitle>Patient*innen transferieren</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={transferPatients}>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="4" htmlFor="clinician-name">
                      Mitarbeitende aktuell
                    </Form.Label>
                    <Col sm="8">
                      <Form.Control
                        id="clinician-name"
                        className="form-control-plain"
                        plaintext
                        readOnly
                        defaultValue={state.clinicianFromUsername}
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group
                    as={Row}
                    className="mb-3"
                    controlId="tranfer-patient"
                  >
                    <Form.Label column sm="4">
                      Transferieren an
                    </Form.Label>
                    <Col sm="8">
                      <Form.Select
                        className="form-control"
                        required
                        value={state.clinician}
                        onChange={handleClinician}
                      >
                        <option>Auswählen...</option>
                        {clinicians &&
                          clinicians.length > 0 &&
                          clinicians.map((clinician) => {
                            return clinician.username !==
                              state.clinicianFromUsername ? (
                              <option
                                disabled={!clinician.enabled}
                                key={clinician.id}
                                value={clinician.username}
                              >
                                {clinician.username}
                              </option>
                            ) : null;
                          })}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                  <Button type="submit">Transferieren</Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeTransferPatientsModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              backdrop="static"
              show={state.modalAssignClinicianOpen}
              onHide={closeAssignClinicianModal}
            >
              <ModalHeader closeButton>
                <ModalTitle>Neue*n Mitarbeitende*n zuweisen</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={handleClinicianAssign}>
                  <Form.Group as={Row} className="mb-3">
                    <Form.Label column sm="5">
                      Patient*in
                    </Form.Label>
                    <Col sm="7">
                      <Form.Control
                        className="form-control-plain"
                        plaintext
                        readOnly
                        defaultValue={state.code}
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group
                    as={Row}
                    className="mb-3"
                    controlId="select-clinician"
                  >
                    <Form.Label column sm="5">
                      Mitarbeitende ändern:
                    </Form.Label>
                    <Col sm="7">
                      <Form.Select
                        className="form-control"
                        required
                        value={state.clinician}
                        onChange={handleClinician}
                      >
                        <option>{state.clinicianFromUsername}</option>
                        {clinicians &&
                          clinicians.length > 0 &&
                          clinicians.map((clinician) => {
                            return clinician.username !==
                              state.clinicianFromUsername ? (
                              <option
                                disabled={!clinician.enabled}
                                key={clinician.id}
                                value={clinician.username}
                              >
                                {clinician.username}
                              </option>
                            ) : null;
                          })}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                  <Button type="submit">Mitarbeitende ändern</Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeAssignClinicianModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal backdrop="static" show={state.modalOpen} onHide={closeModal}>
              <ModalHeader closeButton>
                <ModalTitle>Neue Patient*innen anlegen</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form className="mb-3" noValidate onSubmit={handleSubmit}>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="formGridCode">
                        <Form.Label>SysDok-ID</Form.Label>
                        <Form.Control
                          className="form-control"
                          required
                          type="code"
                          placeholder="SysDok-ID"
                          value={state.code}
                          onChange={handleCode}
                        ></Form.Control>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="formGridBirthday">
                        <Form.Label>Geburtsdatum</Form.Label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Datum auswählen"
                          dateFormat="dd.MM.yyyy"
                          selected={state.birthday || null}
                          onChange={handleBirthdate}
                          onChangeRaw={handleRawDateInput}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Geschlecht</Form.Label>
                        <Form.Select
                          className="form-control"
                          required
                          value={state.gender}
                          onChange={handleGender}
                        >
                          <option>Auswählen...</option>
                          {state.genderList &&
                            state.genderList.length > 0 &&
                            state.genderList.map((gender) => {
                              return (
                                <option key={gender.value} value={gender.value}>
                                  {gender.name}
                                </option>
                              );
                            })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Mitarbeitende zuweisen</Form.Label>
                        <Form.Select
                          className="form-control"
                          required
                          value={state.clinician}
                          onChange={handleClinician}
                        >
                          <option>Auswählen...</option>
                          {clinicians &&
                            clinicians.length > 0 &&
                            clinicians.map((clinicianItem) => {
                              return (
                                <option
                                  disabled={!clinicianItem.enabled}
                                  key={clinicianItem.id}
                                  value={clinicianItem.username}
                                >
                                  {clinicianItem.username}
                                </option>
                              );
                            })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Row className="mb-3">
                      <Col>
                        <Form.Group>
                          <Form.Label>Diagnose</Form.Label>
                          <Typeahead
                            id="basic-typeahead-example"
                            selected={
                              state.diagnosesPatient
                              // Array.isArray(state.patient_diagnoses)
                              //  ? state.diagnoses.filter(i => state.patient_diagnoses.includes(i.id))
                              //  : undefined
                            }
                            multiple={false}
                            labelKey="description"
                            align={"left"}
                            options={diagnoses as Diagnosis[]}
                            placeholder="Auswählen..."
                            clearButton={false}
                            onChange={handleDiagnosis}
                            maxResults={1000}
                            renderMenuItemChildren={(option) => (
                              <span className="text-wrap">
                                <b>{(option as Diagnosis).id}</b> -{" "}
                                {(option as Diagnosis).description}
                              </span>
                            )}
                            filterBy={(option, searchText) => {
                              return (
                                (option as Diagnosis).id
                                  .toLowerCase()
                                  .includes(searchText.text.toLowerCase()) ||
                                (option as Diagnosis).description
                                  .toLowerCase()
                                  .includes(searchText.text.toLowerCase())
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Aufnahmedatum festlegen</Form.Label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Datum auswählen"
                          selected={state.admission || null}
                          onChange={handleAdmissionDate}
                          dateFormat="dd.MM.yyyy"
                          onChangeRaw={handleRawDateInput}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Entlassdatum festlegen</Form.Label>
                        <DatePicker
                          className="form-control"
                          placeholderText="Datum auswählen"
                          selected={state.discharge || null}
                          minDate={state.admission}
                          onChange={handleDischargeDate}
                          dateFormat="dd.MM.yyyy"
                          onChangeRaw={handleRawDateInput}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <PatientLabels
                    labels={state.labels}
                    setLabels={(labels) => updateState({ labels })}
                  />
                  <Button
                    onClick={() => updateState({ editPatientButton: "btn1" })}
                    className="m-1"
                    variant="primary"
                    disabled={isCreatingPatient}
                    type="submit"
                    name="btn1"
                  >
                    Patient*in anlegen
                  </Button>
                  <Button
                    onClick={() => updateState({ editPatientButton: "btn2" })}
                    className="m-1"
                    variant="primary"
                    disabled={isCreatingPatient}
                    type="submit"
                    name="btn2"
                  >
                    Patient*in anlegen + weitere Patient*innen anlegen
                  </Button>
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              backdrop="static"
              show={state.modalBulkOpen}
              onHide={closeBulkModal}
              onClose={closeBulkModal}
            >
              <ModalHeader closeButton>
                <ModalTitle>Sammelimport Patient*innen</ModalTitle>{" "}
              </ModalHeader>
              <ModalBody>
                <Form
                  noValidate
                  onSubmit={
                    !state.bulkValidation
                      ? validateBulkImport
                      : handleBulkImport
                  }
                >
                  <Form.Group controlId="formFile" className="mb-3">
                    <Form.Label>Datei hochladen</Form.Label>
                    <Form.Control
                      type="file"
                      required
                      ref={fileInput}
                      onClick={() => updateState({ bulkValidation: false })}
                    ></Form.Control>
                  </Form.Group>
                  {!state.bulkValidation ? (
                    <Button type="submit" disabled={state.validation}>
                      {" "}
                      {state.validation ? (
                        <div>
                          <Spinner animation="border" /> Liste wird geprüft{" "}
                        </div>
                      ) : (
                        <div>
                          <GoChecklist />
                          Liste überprüfen{" "}
                        </div>
                      )}
                    </Button>
                  ) : (
                    <div>
                      <Button
                        className="m-1"
                        onClick={() =>
                          updateState({ bulkImportButton: "validation" })
                        }
                        type="submit"
                        disabled={state.validation}
                      >
                        {state.validation ? (
                          <div>
                            <Spinner animation="border" /> Liste wird
                            geprüft{" "}
                          </div>
                        ) : (
                          <div>
                            <GoChecklist />
                            Liste überprüfen{" "}
                          </div>
                        )}
                      </Button>
                      <Button
                        className="m-1"
                        disabled={state.importing}
                        onClick={() =>
                          updateState({ bulkImportButton: "upload" })
                        }
                        type="submit"
                      >
                        {state.importing ? (
                          <div>
                            <Spinner animation="border" />
                            Liste wird importiert
                          </div>
                        ) : (
                          <div>
                            <FiUpload /> Liste importieren
                          </div>
                        )}
                      </Button>
                    </div>
                  )}
                </Form>
              </ModalBody>
              <ModalFooter>
                <Button variant="danger" onClick={closeBulkModal}>
                  Abbrechen
                </Button>
              </ModalFooter>
            </Modal>

            <Modal
              backdrop="static"
              show={state.modalValidOpen}
              onHide={closeValidModal}
              size="xl"
            >
              <ModalHeader closeButton>
                <ModalTitle>
                  {state.bulkData.valid ? (
                    <span style={{ color: "green" }}>Alles OK!</span>
                  ) : (
                    <span style={{ color: "red" }}>
                      Die zu importierende csv-Datei enthält Fehler.
                    </span>
                  )}
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                {state.bulkData.valid
                  ? "Es wurden keine Fehler gefunden. Sie können die Patienten importieren."
                  : "Bitte ergänzen Sie fehlende Angaben in leeren Zellen. In der Übersicht werden die fehlerhaften Einträge rot markiert. Diese Einträge müssen Sie in Ihrer Datei anpassen, bevor Sie diese nochmals hochladen. Ein Datum muss im Format JJJJ-MM-TT vorliegen."}
                <Table hover>
                  <thead className="text-dark fw-medium">
                    <tr>
                      <th className="table-without-top text-dark fw-medium">
                        Code
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        Geburtsdatum
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        Geschlecht
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        ID Diagnose
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        Mitarbeitende
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        Aufnahmedatum
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        Entlassdatum
                      </th>
                      <th className="table-without-top text-dark fw-medium">
                        Labels
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.bulkData.patients &&
                      state.bulkData.patients.length > 0 &&
                      state.bulkData.patients.map((patient) => {
                        return (
                          <tr key={patient.items[0].value}>
                            <th
                              className={
                                !patient.items[0].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[0].value}
                            </th>
                            <th
                              className={
                                !patient.items[1].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[1].value}
                            </th>
                            <th
                              className={
                                !patient.items[2].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[2].value}
                            </th>
                            <th
                              className={
                                !patient.items[3].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[3].value}
                            </th>
                            <th
                              className={
                                !patient.items[4].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[4].value}
                            </th>
                            <th
                              className={
                                !patient.items[5].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[5].value}
                            </th>
                            <th
                              className={
                                !patient.items[6].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {patient.items[6].value}
                            </th>
                            <th
                              className={
                                !patient.items[7].valid
                                  ? "not-valid table-without-bottom"
                                  : "table-without-bottom"
                              }
                            >
                              {!patient.items[7].valid
                                ? (() => {
                                    const errorMessage = JSON.stringify(
                                      patient.items[7].message,
                                    );
                                    if (errorMessage.includes("wrong")) {
                                      return "Das Label-Format ist fehlerhaft.";
                                    }
                                  })()
                                : (() => {
                                    const results =
                                      patient.items[7].value.matchAll(
                                        /(?<=value":")(\w+)/g,
                                      );
                                    const resultsArray = Array.from(results);
                                    const matches: string[] = [];
                                    for (const result of resultsArray) {
                                      matches.push(result[0]);
                                    }
                                    return matches.length
                                      ? matches.join(" , ")
                                      : "Value not found";
                                  })()}
                            </th>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>

                <Button variant="primary" onClick={closeValidModal}>
                  Schließen
                </Button>
              </ModalBody>
            </Modal>

            <Modal show={state.modalClinicianOpen} onHide={closeClinicianModal}>
              <ModalHeader closeButton>
                <ModalTitle>Mitarbeitende anlegen</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <Form noValidate onSubmit={handleClinicianRegist}>
                  <Form.Group>
                    <Form.Label>Mitarbeitenden-ID</Form.Label>
                    <Form.Control
                      className="form-control"
                      required
                      type="username"
                      placeholder="Mitarbeitenden-ID"
                      onChange={handleUsername}
                    ></Form.Control>
                  </Form.Group>
                  <Button
                    className="m-1"
                    disabled={isCreatingClinician}
                    type="submit"
                  >
                    Mitarbeitende anlegen
                  </Button>
                </Form>
              </ModalBody>
            </Modal>

            <div>
              <LoadingModal
                modalLoadingOpen={isLoading}
                closeLoadingModal={() => {}}
              />
            </div>
          </div>
        </div>
        <Footer>
          {state.clinicId}
          {state.clinicName}
          {state.username}
          {state.userType}
        </Footer>
      </div>
    </div>
  );
};

export default HomeClinicAdmin;
